import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Button, FormControl, makeStyles, TextField } from "@material-ui/core";
import theme from "../../../themes";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  ChamberInput,
  useCreateChambersMutation,
  UserType,
} from "../../../graphql/types";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useFormik } from "formik";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import useRedirect from "../../common/Hooks/useRedirect";
import {
  ButtonConfigType,
  YesNoButtonConfig,
} from "../../common/ConfirmationModal/ChamberInfoModal";
import { ToastContainer } from "react-toastify";
import ChamberMemberInfoModal from "../../common/ConfirmationModal/ChamberMemberInfoModal";
import { CSVLink } from "react-csv";
import PositionedTooltips from "../../common/utilities/HoverToolTip";
import LogoPicker from "../../common/utilities/LogoPicker";
import ProvincePicker from "../../common/utilities/ProvincePicker";
import PhoneNumberFormat from "../../common/utilities/PhoneNumberFormat";
import ChamberSubscriptionDatePicker from "../../common/utilities/ChamberSubscriptionDatePicker";
import { validWebsiteRegEx } from "../../common/utilities/regexes";
import ChamberSubscriptionPlanRadioGroup from "../../common/ChamberSubscriptionPlanRadioGroup";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";

const CHARACTER_LIMIT_DESCRIPTION = 2000;

const useStyles = makeStyles((themes: any) => ({
  root: {
    backgroundColor: theme.palette.delete.main,
    color: theme.palette.delete.contrastText,
    toolbar: theme.mixins.toolbar,
  },
  background: {},

  title: {
    marginLeft: "24px",
    fontSize: "30px",
    color: "white",
    fontWeight: "bold",
    marginBottom: "30px",
    marginTop: "15px",
    display: "flex",
  },
  contentContainer: {
    height: "100vh",
    backgroundColor: "#F6F6F6",
    fontFamily: "DM Sans, sans-serif",
  },
  boxTitle: {
    fontWeight: "bold",
    fontSize: "4.3vh",
    textAlign: "center",
    marginTop: "11px",
  },
  boxValue: {
    color: "#E1A731",
    fontWeight: "bold",
    fontSize: "21vh",
    textAlign: "center",
  },
  buttonContainer: {
    display: "flex",
    paddingBottom: "9px",
    paddingLeft: "24px",
  },
  titleContent: {
    display: "flex",
    flexDirection: "column-reverse",
    [theme.breakpoints.up("lg")]: {
      paddingTop: "30px",
    },
  },
  chamberContainer: {
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "row-reverse",
    width: "431px",
    margin: "auto",
    backgroundColor: "white",
    marginBottom: "20px",
    borderRadius: "15px",
    [theme.breakpoints.down("sm")]: {
      width: "231px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
    },
  },
  imageContainer: {
    width: "100px",
    height: "100px",
    borderRadius: "200px",
    backgroundColor: "#F2F2F6",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      width: "75px",
      height: "75px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "100px",
      height: "100px",
    },
  },
  images: {
    borderRadius: "200px",
    margin: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "65px",
      height: "65px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "90px",
      height: "90px",
    },
  },
  chamberTitle: {
    margin: "auto",
    alignItems: "center",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1em",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "2em",
    },
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  chamberInput: {
    backgroundColor: "#F2F2F6",
    paddingBottom: "10px",
    borderRadius: "15px",
    marginTop: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "55px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "55px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E1A731",
      },
    },
    "& label.Mui-focused": {
      color: "#E1A731",
    },
    "& label.Mui": {
      color: "black",
    },
  },

  content: {
    background: "#FFFFFF",
    flexGrow: 1,
    borderRadius: "16px",
    margin: "20px",
    flex: "1",
    marginTop: "33px",
    paddingBottom: "33px",
  },
  invalidError: {
    textAlign: "center",
    color: "red",
  },
  chamberInputLogo: {
    display: "flex",
    justifyContent: "flex-end",
    overflow: "hidden",
    padding: "8px",
    outline: "none",
    backgroundColor: "#F2F2F6",
    border: "1.55px solid black",
    borderRadius: "15px",
    paddingLeft: "10px",
    paddingTop: "10px",
    marginTop: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "100px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "100px",
    },
  },
  labelLogo: {
    display: "flex",
    flexDirection: "row-reverse",
    width: "100%",
    justifyContent: "space-between",
    color: "#9A9A9C",
    font: "inherit",
    fontSize: "1.18676em",
  },
  selectedChamberInput: {
    backgroundColor: "#F2F2F6",
    paddingBottom: "10px",
    borderRadius: "15px",
    marginTop: "50px",
    border: "1px solid black",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "55px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "55px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E1A731",
      },
    },
    "& label.Mui-focused": {
      color: "#E1A731",
    },
    "& label.Mui": {
      color: "black",
    },
    "& .makeStyles-selectedValue-57 .MuiSelect-select.MuiSelect-select": {
      paddingLeft: "10px",
    },
  },
  companyInput: {
    backgroundColor: "#F2F2F6",
    paddingBottom: "10px",
    borderRadius: "15px",
    marginTop: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "55px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "55px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E1A731",
      },
    },
    "& label.Mui-focused": {
      color: "#E1A731",
    },
    "& label.Mui": {
      color: "black",
    },
  },
  selectedValue: {
    "& .MuiSelect-select:focus": {
      background: "transparent",
    },
    "& .MuiSelect-select.MuiSelect-select": {
      paddingLeft: "10px",
    },
    "& .MuiSelect-icon": {
      height: "53px",
      backgroundColor: "#E1A731",
      top: "-16px",
      borderRadius: "0px 14px 14px 0px",
      width: "55px",
    },
    "& .MuiSelect-iconOpen": {
      height: "53px",
      backgroundColor: "#E1A731",
      top: "-16px",
      borderRadius: "14px 0px 0px 14px",
      width: "55px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
  },
  companyInputLogo: {
    display: "flex",
    justifyContent: "flex-end",
    overflow: "hidden",
    padding: "8px",
    outline: "none",
    backgroundColor: "#F2F2F6",
    border: "1.55px solid black",
    borderRadius: "15px",
    paddingLeft: "10px",
    paddingTop: "10px",
    marginTop: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "100px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "100px",
    },
  },
  buttonContainers: {
    marginTop: "46px",
    display: "flex",
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-around",
  },
  companyDescription: {
    backgroundColor: "#F2F2F6",
    borderRadius: "15px",
    border: "1px solid black",
    marginTop: "50px",
    paddingLeft: "10px",
    paddingRight: "10px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "105px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "105px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0000008a",
      },
    },
    "& label.Mui-focused": {
      color: "#0000008a",
    },
    "& select": {},
    "& label.Mui": {
      color: "black",
      paddingLeft: "10px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
  },
  companyDescriptionInput: {
    "& .MuiSelect-select:focus": {
      background: "transparent",
    },
    "& .MuiSelect-icon": {
      height: "53px",
      backgroundColor: "#E1A731",
      top: "-16px",
      borderRadius: "0px 7px 7px 0px",
      width: "55px",
    },
    "& .MuiSelect-iconOpen": {
      height: "53px",
      backgroundColor: "#E1A731",
      top: "-16px",
      borderRadius: "0px 0px 0px 0px",
      width: "55px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
    "& .MuiInputBase-inputMultiline": {
      overflow: "scroll !important",
      height: "79px!important",
    },
  },
  companyInputWithToolTipContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: "0px",
  },
}));
const convertToBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};
const CreateChamberAdmin = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [customError, setCustomError] = useState(
    enumStrings.userFacingErrors.saveFailedStaff
  );
  const [invalidError, setInvalidError] = useState(false);
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [chamberName, setChamberName] = useState("");
  const [loading, setLoading] = useState(false);
  const chamberIds = [];
  let createButtonText = enumStrings.CreateChamberMember.Create;
  const [open, setOpen] = useState(false);
  const [createChambers] = useCreateChambersMutation();

  const id = uuidv4();
  chamberIds.push(id);

  const initialValues: ChamberInput = {
    name: "",
    description: "",
    numberOfMembersFromInitialSubscriptionAgreement: 0,
    subscriptionDate: "",
    website: "",
    supportContactEmail: "",
    supportContactName: "",
    supportLink: "",
    phoneNumber: "",
    facebook: "",
    instagram: "",
    threads: "",
    linkedin: "",
    twitter: "",
    tiktok: "",
    youtube: "",
    address: {
      city: "",
      postalCode: "",
      province: "",
      streetAddress: "",
    },
    eventsLink: "",
    newsletterLink: "",
    surveyLink: "",
    advocacyWinsLink: "",
    callForNominationsLink: "",
    memberSignupLink: "",
    logo: undefined,
    id: id,
    jobPostingLink: "",
    adminConsoleLoginEmail: "",
    subscriptionPlanType: undefined,
  };

  const handleClose = (): void => {
    setOpen(false);
  };
  const redirectToManageChamber = useRedirect(RedirectUri.listAllChamberAdmin);
  const yesButtonAction = useCallback(async (): Promise<void> => {
    setLoading(true);
    setOpen(false);
    redirectToManageChamber();
  }, [redirectToManageChamber]);

  const buttonConfig: YesNoButtonConfig = useMemo(() => {
    return {
      buttonType: ButtonConfigType.YesNoConfig,
      noButtonAction: handleClose,
      yesButtonAction,
    };
  }, [yesButtonAction]);

  const validationSchema = yup.object({
    name: yup.string().required(enumStrings.createChamberValidationSchema.name),
    numberOfMembersFromInitialSubscriptionAgreement: yup
      .number()
      .min(
        1,
        enumStrings.createChamberValidationSchema
          .numberOfMembersFromInitialSubscriptionAgreementmin
      )
      .required(
        enumStrings.createChamberValidationSchema
          .numberOfMembersFromInitialSubscriptionAgreement
      ),
    subscriptionDate: yup
      .string()
      .required(enumStrings.createChamberValidationSchema.subscriptionDate),
    website: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      )
      .required(enumStrings.createChamberValidationSchema.website),
    supportContactEmail: yup
      .string()
      .email(enumStrings.createChamberValidationSchema.supportContactEmailemail)
      .required(enumStrings.createChamberValidationSchema.supportContactEmail),
    supportLink: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      ),
    address: yup.object({
      province: yup
        .string()
        .required(enumStrings.createChamberValidationSchema.addressprovince),
      city: yup
        .string()
        .required(enumStrings.createChamberValidationSchema.addresscity),
      postalCode: yup
        .string()
        .required(enumStrings.createChamberValidationSchema.addresspostalCode),
      streetAddress: yup
        .string()
        .required(
          enumStrings.createChamberValidationSchema.addressstreetAddress
        ),
    }),
    adminConsoleLoginEmail: yup
      .string()
      .email(enumStrings.createChamberValidationSchema.supportContactEmailemail)
      .required(
        enumStrings.createChamberValidationSchema.adminConsoleLoginEmail
      ),
    jobPostingLink: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      ),
    eventsLink: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      ),
    newsletterLink: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      ),
    surveyLink: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      ),
    advocacyWinsLink: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      ),
    callForNominationsLink: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      ),
    memberSignupLink: yup
      .string()
      .matches(
        validWebsiteRegEx,
        enumStrings.createChamberValidationSchema.jobPostingLink
      ),
    subscriptionPlanType: yup
      .string()
      .required("Subscription Plan Type is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: ChamberInput) => {
      setLoader(true);
      try {
        setButtonDisabled(true);
        const chamberAdminUser = await createChambers({
          variables: {
            input: {
              data: [
                {
                  address: {
                    city: input.address.city,
                    postalCode: input.address.postalCode,
                    province: input.address.province,
                    streetAddress: input.address.streetAddress,
                  },
                  eventsLink: input.eventsLink,
                  jobPostingLink: input.jobPostingLink,
                  newsletterLink: input.newsletterLink,
                  surveyLink: input.surveyLink,
                  advocacyWinsLink: input.advocacyWinsLink,
                  callForNominationsLink: input.callForNominationsLink,
                  memberSignupLink: input.memberSignupLink,
                  name: input.name,
                  description: input.description,
                  numberOfMembersFromInitialSubscriptionAgreement:
                    input.numberOfMembersFromInitialSubscriptionAgreement,
                  subscriptionDate: input.subscriptionDate,
                  supportContactEmail: input.supportContactEmail
                    .trim()
                    .toLowerCase(),
                  supportContactName: input.supportContactName,
                  supportLink: input.supportLink,
                  phoneNumber: input.phoneNumber,
                  facebook: input.facebook,
                  instagram: input.instagram,
                  threads: input.threads,
                  linkedin: input.linkedin,
                  twitter: input.twitter,
                  tiktok: input.tiktok,
                  youtube: input.youtube,
                  website: input.website,
                  logo: input.logo,
                  id: id,
                  // adding ignore here cuz adminConsoleLoginEmail will ALWAYS be given on creation
                  // @ts-ignore
                  adminConsoleLoginEmail: input.adminConsoleLoginEmail
                    .trim()
                    .toLowerCase(),
                  subscriptionPlanType: input.subscriptionPlanType,
                },
              ],
            },
          },
          refetchQueries: ["getAllChambers"],
        });
        const creationResult = chamberAdminUser.data?.createChambers.results[0];
        if (creationResult?.chamberAddedSuccessfully === false) {
          setInvalidError(true);
          setCustomError(
            `${enumStrings.perkPartnerAdmin.unableToCreateChamber}. ${creationResult.failureReason}`
          );
          createButtonText = enumStrings.createChamberText.error;
          setLoader(false);
          setOpen(false);
          setButtonDisabled(false);
          return;
        }
        const passwordVal = creationResult?.password as string;
        const usernameVal = creationResult?.email as string;
        const chamberNameVal = input.name;
        setPassword(passwordVal);
        setUsername(usernameVal);
        setChamberName(chamberNameVal);
        setOpen(true);
        setLoader(false);
        // Clear the dirty flag so the UnsavedChangesPrompt doesn't trigger after a successful save.
        formik.resetForm({ values: formik.values });
        createButtonText = enumStrings.createChamberText.created;
        const button = document.getElementById("csv-download-button");
        button?.click();
      } catch (error) {
        setOpen(false);
        setInvalidError(true);
        setLoader(false);
        setButtonDisabled(false);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        createButtonText = enumStrings.createChamberText.error;
      }
    },
  });

  // Scroll to first validation error on submit
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const firstErrorField = document.querySelector('.Mui-error, .MuiFormHelperText-root.Mui-error');
      if (firstErrorField) {
        const container = firstErrorField.closest('.MuiFormControl-root') || firstErrorField;
        container.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors]);

  const handleCreateBase64 = useCallback(
    async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      const base64 = await convertToBase64(file);
      formik.setFieldValue("logo", base64);
    },
    [formik]
  );
  const deleteLogo = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    formik.setFieldValue("logo", undefined);
  };

  const csvData = [
    [
      enumStrings.createChamberText.emailUsernameCSVLabel,
      enumStrings.createChamberText.temporaryPassword,
    ],
    [username, password],
  ];

  const rootAdminObj =
    // @ts-ignore
    enumStrings?.groups[UserType.RootAdmin][RedirectUri.createAChamber];
  return (
    <span>
      <div className={classes.titleContent}>
        <span className={classes.title}>
          {/* @ts-ignore */}
          {rootAdminObj?.title}
          <PositionedTooltips
            //  @ts-ignore
            title={rootAdminObj?.title}
            //  @ts-ignore
            description={rootAdminObj?.toolTipDescription}
          />{" "}
        </span>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
        />
      </div>
      <div className={classes.content}>
        <div>
          <form
            className={classes.formContainer}
            onSubmit={formik.handleSubmit}
          >
            <>
              <>
                <ChamberSubscriptionPlanRadioGroup
                  formikSetFieldValue={formik.setFieldValue}
                  formikValues={formik.values}
                  formikErrors={formik.errors}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.chamberName}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
                <div className={classes.companyInputWithToolTipContainer}>
                  <FormControl className={classes.companyDescription}>
                    <TextField
                      type="text"
                      label={enumStrings.createChamberText.chamberDescription}
                      className={classes.companyDescriptionInput}
                      maxRows={6}
                      multiline
                      InputProps={{
                        disableUnderline: true,
                        inputProps: {
                          maxLength: CHARACTER_LIMIT_DESCRIPTION,
                        },
                      }}
                      id="description"
                      name="description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.description &&
                        Boolean(formik.errors.description)
                      }
                      helperText={
                        formik.touched.description && formik.errors.description
                      }
                    />
                  </FormControl>
                </div>
                <ChamberSubscriptionDatePicker
                  formikSetFieldValue={formik.setFieldValue}
                />
                <TextField
                  type="number"
                  label={enumStrings.createChamberText.numberOfMembers}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="numberOfMembersFromInitialSubscriptionAgreement"
                  name="numberOfMembersFromInitialSubscriptionAgreement"
                  value={
                    formik.values
                      .numberOfMembersFromInitialSubscriptionAgreement
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "numberOfMembersFromInitialSubscriptionAgreement",
                      parseInt(e.target.value, 10)
                    );
                    console.log(typeof parseInt(e.target.value, 10));
                  }}
                  error={
                    formik.touched
                      .numberOfMembersFromInitialSubscriptionAgreement &&
                    Boolean(
                      formik.errors
                        .numberOfMembersFromInitialSubscriptionAgreement
                    )
                  }
                  helperText={
                    formik.touched
                      .numberOfMembersFromInitialSubscriptionAgreement &&
                    formik.errors
                      .numberOfMembersFromInitialSubscriptionAgreement
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.website}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="website"
                  name="website"
                  value={formik.values.website}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.website && Boolean(formik.errors.website)
                  }
                  helperText={formik.touched.website && formik.errors.website}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.emailUsername}
                  className={classes.companyInput}
                  variant="outlined"
                  id="adminConsoleLoginEmail"
                  name="adminConsoleLoginEmail"
                  value={formik.values.adminConsoleLoginEmail}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.adminConsoleLoginEmail &&
                    Boolean(formik.errors.adminConsoleLoginEmail)
                  }
                  helperText={
                    formik.touched.adminConsoleLoginEmail &&
                    formik.errors.adminConsoleLoginEmail
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.supportContactName}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="supportContactName"
                  name="supportContactName"
                  value={formik.values.supportContactName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.supportContactName &&
                    Boolean(formik.errors.supportContactName)
                  }
                  helperText={
                    formik.touched.supportContactName &&
                    formik.errors.supportContactName
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.supportContactEmail}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="supportContactEmail"
                  name="supportContactEmail"
                  value={formik.values.supportContactEmail}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.supportContactEmail &&
                    Boolean(formik.errors.supportContactEmail)
                  }
                  helperText={
                    formik.touched.supportContactEmail &&
                    formik.errors.supportContactEmail
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.supportLink}
                  className={classes.companyInput}
                  variant="outlined"
                  id="supportLink"
                  name="supportLink"
                  value={formik.values.supportLink}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.supportLink &&
                    Boolean(formik.errors.supportLink)
                  }
                  helperText={
                    formik.touched.supportLink && formik.errors.supportLink
                  }
                />
                <PhoneNumberFormat
                  formikErrors={formik.errors.phoneNumber}
                  formikVal={formik.values.phoneNumber}
                  handleChange={formik.handleChange}
                  label={enumStrings.createChamberText.phoneNumber}
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  helperText={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.streetAddress}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="address.streetAddress"
                  name="address.streetAddress"
                  value={formik.values.address?.streetAddress}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address?.streetAddress &&
                    Boolean(formik.errors.address?.streetAddress)
                  }
                  helperText={
                    formik.touched.address?.streetAddress &&
                    formik.errors.address?.streetAddress
                  }
                />
                <ProvincePicker
                  formikVal={formik.values.address?.province}
                  formikErrors={
                    formik.touched.address?.province &&
                    Boolean(formik.errors.address?.province)
                  }
                  formikStringError={formik.errors.address?.province}
                  formikSetFieldValue={formik.setFieldValue}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.city}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="address.city"
                  name="address.city"
                  value={formik.values.address?.city}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address?.city &&
                    Boolean(formik.errors.address?.city)
                  }
                  helperText={
                    formik.touched.address?.city && formik.errors.address?.city
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.postalCode}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="address.postalCode"
                  name="address.postalCode"
                  value={formik.values.address?.postalCode}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address?.postalCode &&
                    Boolean(formik.errors.address?.postalCode)
                  }
                  helperText={
                    formik.touched.address?.postalCode &&
                    formik.errors.address?.postalCode
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.facebookUrl}
                  className={classes.companyInput}
                  variant="outlined"
                  id="facebook"
                  name="facebook"
                  value={formik.values.facebook}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.facebook && Boolean(formik.errors.facebook)
                  }
                  helperText={formik.touched.facebook && formik.errors.facebook}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.instagramUrl}
                  className={classes.companyInput}
                  variant="outlined"
                  id="instagram"
                  name="instagram"
                  value={formik.values.instagram}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.instagram && Boolean(formik.errors.instagram)
                  }
                  helperText={
                    formik.touched.instagram && formik.errors.instagram
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.threadsUrl}
                  className={classes.companyInput}
                  variant="outlined"
                  id="threads"
                  name="threads"
                  value={formik.values.threads}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.threads && Boolean(formik.errors.threads)
                  }
                  helperText={formik.touched.threads && formik.errors.threads}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.linkedinUrl}
                  className={classes.companyInput}
                  variant="outlined"
                  id="linkedin"
                  name="linkedin"
                  value={formik.values.linkedin}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.linkedin && Boolean(formik.errors.linkedin)
                  }
                  helperText={formik.touched.linkedin && formik.errors.linkedin}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.twitterUrl}
                  className={classes.companyInput}
                  variant="outlined"
                  id="twitter"
                  name="twitter"
                  value={formik.values.twitter}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.twitter && Boolean(formik.errors.twitter)
                  }
                  helperText={formik.touched.twitter && formik.errors.twitter}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.tiktokUrl}
                  className={classes.companyInput}
                  variant="outlined"
                  id="tiktok"
                  name="tiktok"
                  value={formik.values.tiktok}
                  onChange={formik.handleChange}
                  error={formik.touched.tiktok && Boolean(formik.errors.tiktok)}
                  helperText={formik.touched.tiktok && formik.errors.tiktok}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.youtubeUrl}
                  className={classes.companyInput}
                  variant="outlined"
                  id="youtube"
                  name="youtube"
                  value={formik.values.youtube}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.youtube && Boolean(formik.errors.youtube)
                  }
                  helperText={formik.touched.youtube && formik.errors.youtube}
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.jobPostingLink}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="jobPostingLink"
                  name="jobPostingLink"
                  value={formik.values.jobPostingLink}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.jobPostingLink &&
                    Boolean(formik.errors.jobPostingLink)
                  }
                  helperText={
                    formik.touched.jobPostingLink &&
                    formik.errors.jobPostingLink
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.eventsLink}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="eventsLink"
                  name="eventsLink"
                  value={formik.values.eventsLink}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.eventsLink &&
                    Boolean(formik.errors.eventsLink)
                  }
                  helperText={
                    formik.touched.eventsLink && formik.errors.eventsLink
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.newsletterLink}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="newsletterLink"
                  name="newsletterLink"
                  value={formik.values.newsletterLink}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.newsletterLink &&
                    Boolean(formik.errors.newsletterLink)
                  }
                  helperText={
                    formik.touched.newsletterLink &&
                    formik.errors.newsletterLink
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.surveyLink}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="surveyLink"
                  name="surveyLink"
                  value={formik.values.surveyLink}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.surveyLink &&
                    Boolean(formik.errors.surveyLink)
                  }
                  helperText={
                    formik.touched.surveyLink && formik.errors.surveyLink
                  }
                />
                <TextField
                  type="text"
                  label="Advocacy Wins Link"
                  className={classes.chamberInput}
                  variant="outlined"
                  id="advocacyWinsLink"
                  name="advocacyWinsLink"
                  value={formik.values.advocacyWinsLink}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.advocacyWinsLink &&
                    Boolean(formik.errors.advocacyWinsLink)
                  }
                  helperText={
                    formik.touched.advocacyWinsLink &&
                    formik.errors.advocacyWinsLink
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.callForNominationsLink}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="callForNominationsLink"
                  name="callForNominationsLink"
                  value={formik.values.callForNominationsLink}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.callForNominationsLink &&
                    Boolean(formik.errors.callForNominationsLink)
                  }
                  helperText={
                    formik.touched.callForNominationsLink &&
                    formik.errors.callForNominationsLink
                  }
                />
                <TextField
                  type="text"
                  label={enumStrings.createChamberText.memberSignupLink}
                  className={classes.chamberInput}
                  variant="outlined"
                  id="memberSignupLink"
                  name="memberSignupLink"
                  value={formik.values.memberSignupLink}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.memberSignupLink &&
                    Boolean(formik.errors.memberSignupLink)
                  }
                  helperText={
                    formik.touched.memberSignupLink &&
                    formik.errors.memberSignupLink
                  }
                />
                <LogoPicker
                  logo={formik.values.logo}
                  deleteLogo={deleteLogo}
                  base64={(e: any) => handleCreateBase64(e)}
                />
                <div>
                  {invalidError ? (
                    <div className={classes.invalidError}>{customError}</div>
                  ) : null}
                </div>{" "}
              </>
              <div className={classes.buttonContainers}>
                <CSVLink
                  data={csvData}
                  filename={`${chamberName} Dashboard Credentials.csv`}
                  id="csv-download-button"
                  style={{ display: "none" }}
                />

                <Button
                  type="submit"
                  size="large"
                  color="primary"
                  variant="contained"
                  style={{
                    backgroundColor: "#E1A731",
                    fontSize: "25px",
                    fontWeight: "bold",
                    borderRadius: "10px",
                    width: "200px",
                    marginRight: "25px",
                  }}
                  disabled={buttonDisabled || loader}
                >
                  {loader ? (
                    <CircularProgress />
                  ) : (
                    enumStrings.CreateChamberMember.Create
                  )}
                </Button>
              </div>
            </>
          </form>
        </div>
      </div>
      {open && (
        <ChamberMemberInfoModal
          open={open}
          loading={loading}
          handleClose={handleClose}
          message={enumStrings.messageCreated}
          buttonConfig={buttonConfig}
          password={password}
          username={username}
          companyName={chamberName}
        />
      )}
      <UnsavedChangesPrompt isDirty={formik.dirty} />
    </span>
  );
};

export default CreateChamberAdmin;
